import { FC, useEffect, useMemo, useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useDidMount, useWillUnmount } from "rooks";
import { ResourceMetricsChart } from "@/components/pages/common/resources-ag-grid/components/column-defs/column-def-cell-renderers/metrics/ResourceMetricsChart";
import {
  AllPossibleInlineMetrics,
  MetricsKey,
} from "@/components/pages/common/resources-ag-grid/utils/metrics/metricsTypes";
import { metricCellFrequencyValues } from "@/components/pages/common/resources-ag-grid/utils/metrics/metricsConstants";
import { inlineMetricsHighchartsDataGenerator } from "@/components/pages/common/resources-ag-grid/utils/metrics/inlineMetricsHighchartsDataGenerator";
import { getAggregationByMetricType } from "@/components/pages/common/resources-ag-grid/utils/metrics/getMetricsAggregation";
import { ResourceMetricsAggregation } from "@/components/pages/common/resources-ag-grid/components/column-defs/column-def-cell-renderers/metrics/ResourceMetricsAggregation";
import { getMetricsByFrequency } from "@/components/pages/common/resources-ag-grid/utils/metrics/getMetricsByFrequency";
import { useAppSelector } from "@/store/hooks";
import { resourceGridMetricFrequencySelector } from "@/store/resources-grid/selectors/grid/resourceGridMetricFrequencySelector";
import { ResourceType } from "@/services/cloudchipr.api";

interface ResourceMetricsCellRendererProps extends ICellRendererParams {
  metricKey: MetricsKey;
}

export const ResourceMetricsCellRenderer: FC<
  ResourceMetricsCellRendererProps
> = ({ data, metricKey, column, refreshCell, context }) => {
  const [renderChart, setRenderChart] = useState(false);
  const metricsData: AllPossibleInlineMetrics = data.resource.inline_metrics;
  const resourceType = data.resource.resource_type as ResourceType;
  const masterGridId = context?.masterGridId;

  const metricFrequencyValue = useAppSelector((state) =>
    resourceGridMetricFrequencySelector(
      state,
      resourceType,
      metricKey,
      masterGridId,
    ),
  );

  const metricsByFrequency = getMetricsByFrequency(
    metricKey,
    metricsData,
    metricFrequencyValue,
  );

  const chartData = useMemo(() => {
    if (!metricFrequencyValue) {
      return;
    }

    const frequency = metricCellFrequencyValues[metricFrequencyValue];

    const startPoint =
      frequency === "daily"
        ? metricsData?.daily_start_point
        : metricsData?.hourly_start_point;

    const allIsNull =
      (metricsByFrequency?.split(",")?.filter((item) => item !== "null")
        ?.length ?? 0) === 0;

    if (!metricsByFrequency || allIsNull) {
      return;
    }

    return inlineMetricsHighchartsDataGenerator(
      metricKey,
      { startPoint: startPoint ?? "", data: metricsByFrequency },
      metricFrequencyValue,
    );
  }, [metricsData, metricsByFrequency, metricFrequencyValue, metricKey]);

  const chartDataIsAllNull = useMemo(() => {
    if (!chartData) {
      return true;
    }

    return chartData.series?.at(0)?.data?.every((point) => point.y === null);
  }, [chartData]);

  const aggregation = useMemo(() => {
    return getAggregationByMetricType(
      metricKey,
      metricsByFrequency,
      metricFrequencyValue,
    );
  }, [metricsByFrequency, metricKey, metricFrequencyValue]);

  useDidMount(() => {
    setTimeout(() => setRenderChart(true), 500);
  });

  useWillUnmount(() => {
    setRenderChart(false);
  });

  useEffect(() => {
    if (metricFrequencyValue && refreshCell && column) {
      const key = getMetricFrequencyKeyForLocalContext(metricKey);

      if (column.getColDef().context?.[key]) {
        return;
      }

      column.getColDef().context = {
        ...(column.getColDef().context ?? {}),
        [key]: metricFrequencyValue,
      };

      refreshCell();
    }
  }, [column, refreshCell, metricKey, metricFrequencyValue]);

  if (!renderChart) {
    return (
      <Stack alignItems="end" width="100%" pl={5}>
        <Skeleton height={50} width="100%" />
      </Stack>
    );
  }

  if (!chartData || chartDataIsAllNull) {
    return (
      <Typography variant="caption" color="text.primary" pl={2}>
        N/A
      </Typography>
    );
  }

  return (
    <Stack direction="row" width="100%" spacing={0.5}>
      <ResourceMetricsAggregation
        aggregation={aggregation}
        metricKey={metricKey}
      />

      <Box pt={0.5} width="100%" overflow="hidden">
        <ResourceMetricsChart
          metricKey={metricKey}
          chartData={chartData}
          pointWidth={metricFrequencyValue === "7d" ? 16 : 4}
        />
      </Box>
    </Stack>
  );
};

export const getMetricFrequencyKeyForLocalContext = (metricKey: string) =>
  `${metricKey}_metricFrequencyValue`;
